import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Volume2, VolumeX, Mic, MicOff, MessageSquare, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VocalAvatar = () => {
  const [isListening, setIsListening] = useState(false);
  const [isConversing, setIsConversing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // NEW: Track if AI is processing
  const [currentMessage, setCurrentMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(new Audio());

  const avatarImage = "https://customer-assets.emergentagent.com/job_fb5b119e-cc0c-4fbb-b433-83505048e85c/artifacts/r3x8qxoz__SPD0407.jpg";

  // Auto-greet on first load
  useEffect(() => {
    const hasGreeted = sessionStorage.getItem('olconsult_greeted');
    if (!hasGreeted && !isMuted) {
      const timer = setTimeout(async () => {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/voice/greet`);
          playAudio(response.data.audio_base64);
          setCurrentMessage(response.data.text);
          sessionStorage.setItem('olconsult_greeted', 'true');
        } catch (error) {
          console.error('Greeting error:', error);
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isMuted]);

  const playAudio = (base64Audio) => {
    if (isMuted) {
      // If muted, skip audio but still mark as complete
      setIsSpeaking(false);
      setCurrentMessage('');
      setIsProcessing(false);
      return;
    }
    
    const audio = audioRef.current;
    audio.src = `data:audio/mpeg;base64,${base64Audio}`;
    
    audio.onplay = () => setIsSpeaking(true);
    audio.onended = () => {
      setIsSpeaking(false);
      setCurrentMessage('');
      setIsProcessing(false); // Mark processing complete when audio ends
    };
    
    audio.play().catch(err => {
      console.error('Audio play error:', err);
      setIsSpeaking(false);
      setIsProcessing(false);
    });
  };
  
  // Handle mute toggle
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    if (newMutedState) {
      // Stop any playing audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
  };

  const startConversation = async () => {
    setIsConversing(true);
    setConversationHistory([]);
    setCurrentMessage("Introducing myself...");
    
    try {
      // Get AI introduction
      const introResponse = await axios.post(`${BACKEND_URL}/api/voice/conversation`, {
        message: "Please introduce yourself as the OL-CONSULT virtual assistant.",
        conversation_history: []
      });
      
      // Play introduction
      playAudio(introResponse.data.audio_base64);
      setCurrentMessage(introResponse.data.response);
      
      // After introduction, start listening
      setTimeout(() => {
        if (isConversing) {
          setCurrentMessage("Now, how may I assist you today? Please speak your question.");
          setTimeout(() => startListening(), 1000);
        }
      }, 5000); // Wait 5 seconds for intro to finish
      
    } catch (error) {
      console.error('Introduction error:', error);
      setCurrentMessage("Hello! I'm ready to assist you. Please speak your question.");
      setTimeout(() => startListening(), 2000);
    }
  };

  const stopConversation = () => {
    setIsConversing(false);
    stopListening();
    audioRef.current.pause();
    setIsSpeaking(false);
    setCurrentMessage('');
  };

  const startListening = async () => {
    // Prevent new questions while AI is processing/speaking
    if (isProcessing || isSpeaking) {
      console.log('AI is busy, please wait...');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsListening(true);
      
      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopListening();
        }
      }, 10000);
      
    } catch (error) {
      console.error('Microphone error:', error);
      setCurrentMessage('Microphone access denied. Please enable microphone and try again.');
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    setIsListening(false);
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true); // Mark as processing to block new questions
    setCurrentMessage('Processing your question...');
    
    try {
      // Speech to text
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      
      const sttResponse = await axios.post(
        `${BACKEND_URL}/api/voice/speech-to-text`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const userMessage = sttResponse.data.transcription;
      setCurrentMessage(`You asked: "${userMessage}"`);
      
      // Update conversation history
      const newHistory = [
        ...conversationHistory,
        { role: 'user', content: userMessage }
      ];
      
      // Get AI response
      const aiResponse = await axios.post(`${BACKEND_URL}/api/voice/conversation`, {
        message: userMessage,
        conversation_history: conversationHistory
      });
      
      // Play response (will set isProcessing to false when done)
      playAudio(aiResponse.data.audio_base64);
      setCurrentMessage(aiResponse.data.response);
      
      // Update history with assistant response
      setConversationHistory([
        ...newHistory,
        { role: 'assistant', content: aiResponse.data.response }
      ]);
      
      // Wait for audio to finish before accepting new questions
      const audio = audioRef.current;
      audio.onended = () => {
        setIsSpeaking(false);
        setCurrentMessage('');
        setIsProcessing(false);
        
        // Ready for next question only after response is complete
        if (isConversing && !isMuted) {
          setTimeout(() => {
            setCurrentMessage("I'm ready for your next question...");
            setTimeout(() => startListening(), 1000);
          }, 1000);
        }
      };
      
    } catch (error) {
      console.error('Processing error:', error);
      setCurrentMessage('Sorry, I had trouble understanding. Please try again.');
      setIsProcessing(false);
      if (isConversing) {
        setTimeout(() => startListening(), 2000);
      }
    }
  };

  return (
    <div className="fixed left-1 sm:left-4 md:left-6 bottom-1 sm:bottom-4 md:bottom-6 z-40">
      <Card className="bg-white shadow-2xl border-2 border-blue-100 hover:shadow-blue-500/20 transition-all duration-300 max-w-[200px] sm:max-w-[260px] md:max-w-sm">
        <div className={`${isMinimized ? 'p-1.5 sm:p-2 md:p-3' : 'p-2 sm:p-3 md:p-4'} space-y-1 sm:space-y-2 md:space-y-3`}>
          {/* Header - Always visible */}
          <div className="flex items-center justify-between">
            <div className={`${isMinimized ? 'text-[10px] sm:text-xs' : 'text-[10px] sm:text-xs'} font-semibold text-gray-900 flex items-center gap-1 sm:gap-2`}>
              {isMinimized ? (
                <>
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-blue-900" />
                  <span className="hidden sm:inline">AI Assistant</span>
                  <span className="sm:hidden">AI</span>
                </>
              ) : (
                <span className="hidden sm:inline">Virtual Assistant</span>
              )}
            </div>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-0.5 sm:p-1 md:p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title={isMinimized ? "Expand Assistant" : "Minimize Assistant"}
            >
              {isMinimized ? (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              ) : (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              )}
            </button>
          </div>

          {isMinimized ? (
            /* Minimized View - Compact */
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 ${
                isSpeaking ? 'border-green-500 animate-pulse' : 
                isListening ? 'border-red-500 animate-pulse' :
                isConversing ? 'border-blue-500' :
                'border-blue-900'
              } transition-all duration-300`}>
                <img
                  src={avatarImage}
                  alt="OL Consult Assistant"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] sm:text-xs font-semibold text-gray-900">
                  {isListening ? '🎤 Listening' : 
                   isSpeaking ? '🔊 Speaking' : 
                   isConversing ? '💬 Active' :
                   '👋 Ready'}
                </p>
                {currentMessage && (
                  <p className="text-[9px] sm:text-xs text-gray-600 line-clamp-1 mt-0.5">
                    {currentMessage.length > 30 ? currentMessage.substring(0, 30) + '...' : currentMessage}
                  </p>
                )}
              </div>
            </div>
          ) : (
            /* Full View - Expanded */
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              {/* Avatar */}
              <div className="relative flex justify-center">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 ${
                  isSpeaking ? 'border-green-500 animate-pulse' : 
                  isListening ? 'border-red-500 animate-pulse' :
                  'border-blue-900'
                } transition-all duration-300`}>
                  <img
                    src={avatarImage}
                    alt="OL Consult Assistant"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Status indicator */}
                {(isSpeaking || isListening) && (
                  <div className={`absolute -bottom-1 -right-1 w-6 h-6 ${
                    isSpeaking ? 'bg-green-500' : 'bg-red-500'
                  } rounded-full border-2 border-white flex items-center justify-center`}>
                    {isSpeaking ? (
                      <Volume2 className="w-3 h-3 text-white" />
                    ) : (
                      <Mic className="w-3 h-3 text-white animate-pulse" />
                    )}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-center">
                <h4 className="font-semibold text-gray-900 text-xs sm:text-sm">AI Voice Assistant</h4>
                <p className="text-[10px] sm:text-xs text-gray-600">OL-CONSULT SARL</p>
              </div>

              {/* Status Message */}
              {currentMessage && (
                <div className="bg-blue-50 p-1.5 sm:p-2 rounded-lg">
                  <p className="text-[10px] sm:text-xs text-gray-700 text-center leading-relaxed line-clamp-2 sm:line-clamp-3">
                    {currentMessage}
                  </p>
                </div>
              )}

              {/* Controls */}
              <div className="space-y-1.5 sm:space-y-2">
                {!isConversing ? (
                  <Button
                    onClick={startConversation}
                    className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white text-xs sm:text-sm py-1.5 sm:py-2"
                  >
                    <Mic className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Start Conversation</span>
                    <span className="sm:hidden">Start</span>
                  </Button>
                ) : (
                  <div className="flex gap-1.5 sm:gap-2">
                    {isListening ? (
                      <Button
                        onClick={stopListening}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-1.5 sm:py-2"
                      >
                        <MicOff className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">Stop</span>
                      </Button>
                    ) : (
                      <Button
                        onClick={startListening}
                        disabled={isSpeaking}
                        className="flex-1 bg-blue-900 hover:bg-blue-800 text-white text-xs sm:text-sm py-1.5 sm:py-2"
                      >
                        <Mic className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden sm:inline">Speak</span>
                      </Button>
                    )}
                    <Button
                      onClick={stopConversation}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50 text-sm py-2"
                    >
                      End
                    </Button>
                  </div>
                )}
                
                <Button
                  onClick={toggleMute}
                  size="sm"
                  variant="outline"
                  className="w-full border-blue-900 text-blue-900 hover:bg-blue-50 text-xs"
                  disabled={isProcessing && isSpeaking}
                >
                  {isMuted ? (
                    <><VolumeX className="w-3 h-3 mr-1" /> Unmute</>
                  ) : (
                    <><Volume2 className="w-3 h-3 mr-1" /> Mute</>
                  )}
                </Button>
              </div>

              {/* Status */}
              <p className="text-xs text-center text-gray-500">
                {isProcessing ? '⏳ Processing...' :
                 isListening ? '🎤 Listening...' : 
                 isSpeaking ? '🔊 Speaking...' : 
                 isConversing ? '💬 In conversation' :
                 '👋 Ready to assist'}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default VocalAvatar;
